import { useCallback, useEffect, useRef, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { getEmptyAddress } from "@jti/commerce";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { GoogleAPIProvider } from "@jti/fe-clients";
import { isAddressFormExtendedWithProvince } from "@jti/fe-common";
import type { IGetOrderDeliveryLimits } from "@jti/magento";
import { useRegions } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectAddresses } from "@jti/store/selectors/selectAddresses";
import { selectPostCodeConfig } from "@jti/store/selectors/selectPostCodeConfig";
import {
  clearShippingSelectedAddressId,
  setShippingSelectedAddressId,
  toggleSelectShippingAddress,
} from "@jti/store/slices/LEGACY__checkoutSlice";
import { type CheckoutShippingStepData } from "@jti/store/slices/standardCheckoutFormSlice";
import { AddressFormProvider, AddressInfoBlock, useApplyAddressOverrides, useTranslations } from "@jti/ui";

import { OrderAttributes } from "@checkout/components/OrderAttributes";
import { isInvalidPostCode } from "@checkout/utils";

import { SHIPPING_FORM_FIELDS } from "../../constants";
import { useAddressRegionMapping } from "../../hooks/useAddressRegionMapping";
import { useShippingFormContext } from "../../hooks/useShippingFormContext";
import { SelectShippingAddressDialog } from "../SelectShippingAddressDialog";

import styles from "./ShippingAddress.module.scss";

type ShippingAddressProps = {
  orderDeliveryLimits: IGetOrderDeliveryLimits;
  isExternalValidationShippingAddressErrorVisible: boolean;
};

type ShippingAddressMode = "info" | "new";

export const ShippingAddress = ({
  orderDeliveryLimits,
  isExternalValidationShippingAddressErrorVisible,
}: ShippingAddressProps) => {
  const locale = useLocale();
  const dispatch = useDispatch();
  const { __ } = useTranslations();
  const config = useConfig();
  const postCodeRef = useRef<string>("");

  const postCodeConfig = useSelector(selectPostCodeConfig);
  const addresses = useSelector(selectAddresses);
  const country = useSelector((state) => state.checkout.country);

  const formContext = useShippingFormContext();
  const { getValues, setValue, watch, trigger, clearErrors, setError, formState } = formContext;
  const { errors } = formState;
  const shippingAddressError = errors.root?.[SHIPPING_FORM_FIELDS.shippingAddress];
  const atHomeDeliveryShippingAddress = watch(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress);

  const { applyAddressOverrides } = useApplyAddressOverrides();
  const immutableProvinceCode = watch("_hiddenValues.immutableProvinceCode");

  const isAddressExtendedWithProvince = isAddressFormExtendedWithProvince(config.addressFormVariant);

  const [mode, setMode] = useState<ShippingAddressMode>(atHomeDeliveryShippingAddress ? "info" : "new");

  const showShippingAddressSelector = useCallback(() => {
    dispatch(toggleSelectShippingAddress(true));
  }, [dispatch]);

  const onCreateNewAddress = useCallback(() => {
    setMode("new");

    const currentAddress = getValues(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress);

    setValue(
      SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress,
      getEmptyAddress({
        currentAddress,
        overrides: {
          countryCode: country.countryCode,
          countryName: country.countryName,
          region: isAddressExtendedWithProvince ? currentAddress?.region : undefined,
          regionCode: isAddressExtendedWithProvince ? currentAddress?.regionCode : undefined,
        },
      })
    );

    dispatch(clearShippingSelectedAddressId());
    clearErrors(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress);
  }, [
    clearErrors,
    country.countryCode,
    country.countryName,
    dispatch,
    getValues,
    setValue,
    isAddressExtendedWithProvince,
  ]);

  const onSelectExistingAddress = useCallback(
    (addressId: string) => {
      const selectedAddress = addresses.find((address) => address.id === addressId);

      if (!selectedAddress) {
        return;
      }

      setValue(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress, selectedAddress);
      clearErrors(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress);

      dispatch(setShippingSelectedAddressId(addressId));

      setMode("info");
    },
    [setMode, addresses]
  );

  useEffect(() => {
    const shouldSetPostCodeError =
      mode === "info" && postCodeConfig && isInvalidPostCode(atHomeDeliveryShippingAddress?.postcode, postCodeConfig);
    if (!shouldSetPostCodeError) {
      clearErrors(`${SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress}.postcode`);
      return;
    }

    setError(`${SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress}.postcode`, {
      type: "manual",
      message: __("form_validation.postcode_from_account_incorrect"),
    });

    trigger();
  }, [atHomeDeliveryShippingAddress?.postcode, setError, clearErrors, trigger, mode, postCodeConfig, __]);

  useEffect(
    function clearServerErrors() {
      if (postCodeRef.current !== atHomeDeliveryShippingAddress?.postcode && errors.root) {
        clearErrors("root");
      }

      postCodeRef.current = atHomeDeliveryShippingAddress?.postcode;
    },
    [mode, clearErrors, atHomeDeliveryShippingAddress?.postcode, errors.root]
  );

  useEffect(
    function handleExternalShippingAddressValidationErrorClearing() {
      const subscription = watch((_value, { name }) => {
        if (!name) {
          return;
        }
        const isAtHomeDeliveryShippingFieldUpdate = name.startsWith(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress);

        if (isAtHomeDeliveryShippingFieldUpdate && shippingAddressError) {
          clearErrors(`root.${SHIPPING_FORM_FIELDS.shippingAddress}`);
        }
      });
      return () => subscription.unsubscribe();
    },
    [clearErrors, watch, shippingAddressError]
  );

  useEffect(() => {
    if (mode === "info" || (mode === "new" && atHomeDeliveryShippingAddress)) {
      return;
    }

    Object.entries(country).forEach(([key, value]) => {
      setValue(`atHomeDeliveryShippingAddress.${key}` as keyof CheckoutShippingStepData, value);
    });
  }, [mode, atHomeDeliveryShippingAddress, country, setValue]);

  const hasRegionField =
    config.addressFormVariant === "with_regions" || config.addressFormVariant === "with_regions_reversed";

  const { regions } = useRegions(config.ISO_COUNTRY_CODE, hasRegionField);

  useAddressRegionMapping(formContext, SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress, regions);

  return (
    <>
      <Card
        data-testid="shipping-address-card"
        title={`${__("cart.checkout.shipping_address.title")} ${mode === "new" ? "*" : ""} `}
        variant="level-1"
      >
        <OrderAttributes target={"shippingAddress_before"} />

        {!isExternalValidationShippingAddressErrorVisible && shippingAddressError ? (
          <div className={styles.messageBox}>
            <AlertBanner
              tone="warning"
              data-testid="magento-shipping-error"
              description={shippingAddressError.message}
            />
          </div>
        ) : null}

        {errors.atHomeDeliveryShippingAddress?.postcode?.type === "manual" ? (
          <div className={styles.messageBox}>
            <AlertBanner
              tone="warning"
              data-testid="postcode-error-message-box"
              description={errors.atHomeDeliveryShippingAddress.postcode.message}
            />
          </div>
        ) : null}

        {mode === "new" && (
          <GoogleAPIProvider
            apiKey={config.GOOGLE_MAPS_API}
            options={{
              version: "weekly",
              libraries: ["geometry", "places"],
              language: locale,
              region: config.ISO_COUNTRY_CODE,
            }}
          >
            <AddressFormProvider
              data-testid="shipping-address-field"
              orderDeliveryLimits={orderDeliveryLimits}
              locationServiceAutocompleteProvider={config.locationServiceAutocompleteProvider}
              formType={config.addressFormVariant}
              fieldName={(name: string) => `atHomeDeliveryShippingAddress.${name}`}
              postCodeConfig={postCodeConfig}
              regions={regions}
              isSingleFieldAddressLookup={config.isSingleFieldAddressLookup}
              isStreetFieldCombined={config.isStreetFieldCombined}
              ksaProvinces={config.ksaProvinces}
            />
          </GoogleAPIProvider>
        )}

        {mode === "info" && atHomeDeliveryShippingAddress && (
          <AddressInfoBlock
            address={applyAddressOverrides(atHomeDeliveryShippingAddress, {
              provinceCode: immutableProvinceCode,
            })}
            hideName
            hideTelephone
            isEditable
            editLabel={__("cart.checkout.shipping_address.edit")}
            onEdit={showShippingAddressSelector}
          />
        )}

        <OrderAttributes target={"shippingAddress_after"} />

        {mode === "new" && (
          <div className={styles.bottomAction}>
            <Button variant="text" onClick={showShippingAddressSelector} data-testid="bottomActionButton">
              {__("cart.checkout.shipping_address.edit")}
            </Button>
          </div>
        )}
      </Card>

      <SelectShippingAddressDialog
        onCreateAddress={onCreateNewAddress}
        onSelectAddress={onSelectExistingAddress}
        defaultSelectedAddressId={atHomeDeliveryShippingAddress?.id}
        provinceCode={immutableProvinceCode}
      />
    </>
  );
};
